import React, { useEffect, useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { ProfileCard } from './ProfileCard';
import { Heart, MessageCircle, FileText, Image } from 'lucide-react';

interface ConstellationItem {
  id: string;
  type: 'profile' | 'post' | 'image_post';
  position: { x: number; y: number };
  data: any;
  connections: string[];
  cluster?: string;
}

interface ConstellationLayoutProps {
  query: string;
  results: any[];
  onContentClick: (id: string) => void;
  onContentLike: (id: string) => void;
  onProfileInteract: (action: 'follow' | 'message' | 'view', profileId: string) => void;
}

export const ConstellationLayout: React.FC<ConstellationLayoutProps> = ({
  query,
  results,
  onContentClick,
  onContentLike,
  onProfileInteract
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [constellationItems, setConstellationItems] = useState<ConstellationItem[]>([]);
  const [connections, setConnections] = useState<{ from: string; to: string; strength: number }[]>([]);
  const [containerSize, setContainerSize] = useState({ width: 1200, height: 800 });

  // Calculate dynamic positioning around the search area
  useEffect(() => {
    if (!results.length) return;

    const updateSize = () => {
      if (containerRef.current) {
        setContainerSize({
          width: containerRef.current.offsetWidth,
          height: Math.max(800, window.innerHeight - 300)
        });
      }
    };

    updateSize();
    window.addEventListener('resize', updateSize);
    return () => window.removeEventListener('resize', updateSize);
  }, []);

  useEffect(() => {
    if (!results.length) return;

    const items: ConstellationItem[] = [];
    const newConnections: { from: string; to: string; strength: number }[] = [];

    // Define constellation clusters based on content type and relevance
    const clusters = {
      profiles: { centerX: 0.2, centerY: 0.3, radius: 200 },
      content: { centerX: 0.8, centerY: 0.4, radius: 180 },
      media: { centerX: 0.5, centerY: 0.7, radius: 160 }
    };

    results.forEach((item, index) => {
      let cluster: keyof typeof clusters = 'content';
      if (item.type === 'profile') cluster = 'profiles';
      else if (item.type === 'image_post') cluster = 'media';

      const clusterInfo = clusters[cluster];
      const angle = (index * (360 / results.length)) * Math.PI / 180;
      const radius = clusterInfo.radius + (Math.random() - 0.5) * 100;
      
      // Calculate position with some randomness for organic feel
      const position = {
        x: containerSize.width * clusterInfo.centerX + Math.cos(angle) * radius,
        y: containerSize.height * clusterInfo.centerY + Math.sin(angle) * radius
      };

      // Ensure items stay within bounds
      position.x = Math.max(150, Math.min(containerSize.width - 150, position.x));
      position.y = Math.max(100, Math.min(containerSize.height - 100, position.y));

      items.push({
        id: item.id,
        type: item.type,
        position,
        data: item,
        connections: [],
        cluster
      });

      // Create connections based on similarity (simplified)
      if (index > 0) {
        const shouldConnect = Math.random() > 0.7 || 
          (item.type === results[index - 1]?.type && Math.random() > 0.4);
        
        if (shouldConnect) {
          newConnections.push({
            from: results[index - 1].id,
            to: item.id,
            strength: 0.5 + Math.random() * 0.5
          });
        }
      }
    });

    setConstellationItems(items);
    setConnections(newConnections);
  }, [results, containerSize]);

  const renderContentCard = (item: ConstellationItem) => {
    const { data } = item;

    if (data.type === 'profile') {
      return (
        <ProfileCard
          id={data.id}
          username={data.profileData?.username}
          displayName={data.profileData?.displayName}
          bio={data.profileData?.bio}
          profileImageUrl={data.profileData?.profileImageUrl}
          score={data.score}
          isDiscoverable={data.profileData?.isDiscoverable}
          onInteract={onProfileInteract}
        />
      );
    }

    // Post cards
    return (
      <div className="bg-card/90 backdrop-blur-sm border border-border/50 rounded-lg p-4 hover:border-primary/50 transition-all duration-300 hover:shadow-lg max-w-[280px]">
        <div className="space-y-3">
          {/* Post header */}
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 bg-muted rounded-full flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <h4 className="font-medium text-xs">{data.user.name}</h4>
              <p className="text-xs text-muted-foreground">@{data.user.username}</p>
            </div>
            {data.type === 'image_post' ? 
              <Image className="w-4 h-4 text-muted-foreground" /> :
              <FileText className="w-4 h-4 text-muted-foreground" />
            }
          </div>

          {/* Image for image posts */}
          {data.type === 'image_post' && data.image && (
            <div className="w-full h-32 bg-muted rounded overflow-hidden">
              <img 
                src={data.image} 
                alt="Post content" 
                className="w-full h-full object-cover"
              />
            </div>
          )}

          {/* Content */}
          <p className="text-xs text-foreground/90 leading-relaxed line-clamp-3">
            {data.content}
          </p>

          {/* Engagement */}
          <div className="flex items-center gap-3 pt-2 border-t border-border/50">
            <div 
              className="flex items-center gap-1 hover:text-red-500 transition-colors cursor-pointer"
              onClick={(e) => {
                e.stopPropagation();
                onContentLike(data.id);
              }}
            >
              <Heart className="w-3 h-3" />
              <span className="text-xs">{data.stats?.likes || 0}</span>
            </div>
            <div className="flex items-center gap-1 hover:text-blue-500 transition-colors cursor-pointer">
              <MessageCircle className="w-3 h-3" />
              <span className="text-xs">{data.stats?.comments || 0}</span>
            </div>
            <div className="ml-auto text-xs text-primary font-medium">
              {Math.round((data.score || 0) * 100)}%
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div ref={containerRef} className="relative w-full overflow-hidden" style={{ height: containerSize.height }}>
      {/* Connection lines SVG */}
      <svg 
        className="absolute inset-0 pointer-events-none z-0"
        width={containerSize.width}
        height={containerSize.height}
      >
        {connections.map((connection, i) => {
          const fromItem = constellationItems.find(item => item.id === connection.from);
          const toItem = constellationItems.find(item => item.id === connection.to);
          
          if (!fromItem || !toItem) return null;

          return (
            <motion.line
              key={`connection-${i}`}
              x1={fromItem.position.x}
              y1={fromItem.position.y}
              x2={toItem.position.x}
              y2={toItem.position.y}
              stroke="hsl(var(--primary))"
              strokeWidth={1}
              opacity={connection.strength * 0.4}
              initial={{ pathLength: 0, opacity: 0 }}
              animate={{ pathLength: 1, opacity: connection.strength * 0.4 }}
              transition={{ duration: 1, delay: i * 0.1 }}
            />
          );
        })}
      </svg>

      {/* Constellation items */}
      {constellationItems.map((item, index) => (
        <motion.div
          key={item.id}
          className="absolute z-10 cursor-pointer"
          style={{
            left: item.position.x,
            top: item.position.y,
            transform: 'translate(-50%, -50%)'
          }}
          initial={{ 
            scale: 0, 
            opacity: 0,
            rotate: Math.random() * 360 
          }}
          animate={{ 
            scale: 1, 
            opacity: 1,
            rotate: 0
          }}
          transition={{ 
            duration: 0.6, 
            delay: index * 0.1,
            type: "spring",
            damping: 0.7
          }}
          whileHover={{ 
            scale: 1.05,
            z: 20
          }}
          onClick={() => onContentClick(item.id)}
        >
          {renderContentCard(item)}
          
          {/* Floating animation */}
          <motion.div
            className="absolute inset-0 pointer-events-none"
            animate={{
              y: [-2, 2, -2],
            }}
            transition={{
              duration: 3 + Math.random() * 2,
              repeat: Infinity,
              ease: "easeInOut"
            }}
          />
        </motion.div>
      ))}

      {/* Cluster labels */}
      <motion.div
        className="absolute top-4 left-4 text-xs text-muted-foreground bg-background/80 backdrop-blur-sm rounded-full px-3 py-1"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1 }}
      >
        👤 Profiles
      </motion.div>
      
      <motion.div
        className="absolute top-4 right-4 text-xs text-muted-foreground bg-background/80 backdrop-blur-sm rounded-full px-3 py-1"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.2 }}
      >
        📝 Content
      </motion.div>
      
      <motion.div
        className="absolute bottom-4 left-1/2 transform -translate-x-1/2 text-xs text-muted-foreground bg-background/80 backdrop-blur-sm rounded-full px-3 py-1"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4 }}
      >
        🎨 Media
      </motion.div>
    </div>
  );
};